/**
 * Bottom sheet that opens when the user taps GroupOfferBanner.
 *
 * Two views, toggled by `mode`:
 *   - "start"  → name input + "Start group order" CTA → createGroup()
 *   - "join"   → 4-char code input + "Join group" CTA → joinByCode()
 *
 * The user can switch from start → join via "I have an invite code". On
 * success the parent closes the sheet (we navigate back to /home which
 * re-renders showing GroupBanner instead of GroupOfferBanner).
 */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "./BottomSheet";
import { GroupIllo } from "./GroupIllo";
import { useGroupCart } from "@/store/group-cart";

type Mode = "start" | "join";

export interface GroupStartSheetProps {
  open: boolean;
  onClose: () => void;
  /** Fired after a successful host-side `createGroup`. The parent typically
   *  opens GroupSheet so the host can immediately share the invite code. */
  onCreated?: () => void;
}

export function GroupStartSheet({ open, onClose, onCreated }: GroupStartSheetProps) {
  const { t } = useTranslation();
  const { createGroup, joinByCode } = useGroupCart();
  const [mode, setMode] = useState<Mode>("start");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (submitting) return;
    setMode("start");
    setName("");
    setCode("");
    setError(null);
    onClose();
  };

  const handleStart = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await createGroup({ displayName: name.trim() });
      handleClose();
      onCreated?.();
    } catch (err) {
      setError((err as Error).message ?? "Failed to start group");
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoin = async (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!name.trim() || c.length !== 4 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await joinByCode(c, { displayName: name.trim() });
      handleClose();
    } catch (err) {
      setError((err as Error).message ?? "Failed to join");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      ariaLabel={t("v2.group_cart.start_title")}
    >
      <form
        onSubmit={mode === "start" ? handleStart : handleJoin}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div style={{ alignSelf: "center" }}>
          <GroupIllo size={140} />
        </div>

        <div style={{ textAlign: "center" }}>
          <div className="display" style={{ fontSize: 20, fontWeight: 800, color: "var(--ink-1)" }}>
            {t("v2.group_cart.start_title")}
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4 }}>
            {t("v2.group_cart.start_sub")}
          </div>
        </div>

        <label
          style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "var(--ink-2)",
              letterSpacing: 0.2,
            }}
          >
            {t("v2.group_cart.your_name")}
          </span>
          <input
            data-testid="group-start-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("v2.group_cart.your_name_placeholder")}
            maxLength={32}
            autoFocus
            style={{
              height: 44,
              padding: "0 14px",
              borderRadius: "var(--radius-card)",
              border: "1px solid var(--border)",
              background: "var(--surface-1)",
              fontSize: 15,
              outline: "none",
            }}
          />
        </label>

        {mode === "join" && (
          <label
            style={{ display: "flex", flexDirection: "column", gap: 6 }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-2)",
                letterSpacing: 0.2,
              }}
            >
              {t("v2.group_cart.invite_code_label")}
            </span>
            <input
              data-testid="group-start-code"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.toUpperCase().slice(0, 4))
              }
              placeholder={t("v2.group_cart.invite_code_placeholder")}
              inputMode="text"
              autoCapitalize="characters"
              maxLength={4}
              style={{
                height: 48,
                padding: "0 14px",
                borderRadius: "var(--radius-card)",
                border: "1px solid var(--border)",
                background: "var(--surface-1)",
                fontSize: 22,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, monospace",
                letterSpacing: 6,
                textAlign: "center",
                outline: "none",
                textTransform: "uppercase",
              }}
            />
          </label>
        )}

        {error && (
          <div
            role="alert"
            style={{
              fontSize: 12,
              color: "#b91c1c",
              background: "#fee2e2",
              padding: "8px 12px",
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}

        <button
          data-testid="group-start"
          type="submit"
          disabled={
            submitting ||
            !name.trim() ||
            (mode === "join" && code.trim().length !== 4)
          }
          style={{
            width: "100%",
            height: 48,
            borderRadius: 12,
            background:
              submitting ||
              !name.trim() ||
              (mode === "join" && code.trim().length !== 4)
                ? "var(--surface-2-strong, #e5e7eb)"
                : "var(--primary-600)",
            color: "#fff",
            border: "none",
            fontSize: 15,
            fontWeight: 700,
            cursor: submitting ? "wait" : "pointer",
          }}
        >
          {submitting
            ? mode === "join"
              ? t("v2.group_cart.join_loading")
              : "…"
            : mode === "start"
              ? t("v2.group_cart.start_cta")
              : t("v2.group_cart.join_cta")}
        </button>

        <button
          data-testid="group-have-code"
          type="button"
          onClick={() => {
            setMode(mode === "start" ? "join" : "start");
            setError(null);
          }}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--primary-700)",
            fontSize: 13,
            fontWeight: 700,
            padding: "4px 0",
            cursor: "pointer",
          }}
        >
          {mode === "start"
            ? t("v2.group_cart.have_invite")
            : t("v2.group_cart.start_cta")}
        </button>
      </form>
    </BottomSheet>
  );
}
